/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useTranslate } from "@adapptio/lib-components";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { termsEditorCommon as terms } from "../../../terms/common";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { CreateDirectoryDialog } from "./CreateDirectoryDialog";
import { FieldText } from "../../../ui/components/Fields/FieldText";
import { Typography } from "../../../ui/components/Typography";
import { DialogContent } from "../../../ui/components/Dialogs/DialogContent";
import { useEnhancedEffect, useFieldState } from "../../../ui/hooks";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { Form } from "../../../ui/components/Form";
import { IResourceListProps, IResourceNode, ResourceList } from "../../../ui/components/ResourceList";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { useDeepCompareMemo } from "use-deep-compare";
import { useEditorMainContext } from "../../../ui/contexts/EditorMainContext";

const allowedResourceTypes: RESOURCE_TYPES[] = [ RESOURCE_TYPES.DIRECTORY ];

export const CreateDirectoryDialogComponent: TDialogComponent<CreateDirectoryDialog> = ({ dialog }) => {
	const { appInfo } = useEditorMainContext();
	const inputRef = useRef<HTMLInputElement>();
	const t = useTranslate();
	const params = dialog.getParams();
	const defaultValue = params?.defaultValue ?? "";

	const translations = {
		title: t("editor", terms.dialog.createDirectory.title),
		controls: {
			name: t("editor", terms.dialog.createDirectory.controls.name.label),
			directory: t("editor", terms.dialog.createDirectory.controls.directory.label)
		},
		actions: {
			confirm: t("editor", terms.dialog.createDirectory.buttonConfirm),
			cancel: t("editor", terms.dialog.createDirectory.buttonCancel)
		}
	};

	const baseClass = BEM("edt-module-core-create-directory-dialog");

	const [ selectedParentNode, setSelectedParentNode ] = useState<IResourceNode>(null);

	const basePath = useDeepCompareMemo(() => {
		if (selectedParentNode && !selectedParentNode.isRoot) {
			return normalizeResourcePath(selectedParentNode.basePath, selectedParentNode.name);
		}

		return "";
	}, [ selectedParentNode ]);

	const validators = useMemo(() => params.getValidators({ basePath }), [ basePath ]);

	const [ fieldState, setFieldState ] = useFieldState(
		{
			name: {
				defaultValue,
				validators
			}
		},
		[ dialog, validators, defaultValue, basePath ]
	);

	const selectResource = useCallback<IResourceListProps["onSelect"]>((selectedNode) => {
		setSelectedParentNode(selectedNode);
	}, []);

	const handleClose = useCallback(() => {
		dialog.close({
			name: null,
			basePath: null
		});
	}, [ dialog ]);

	const handleSubmit = useCallback(() => {
		const name = fieldState.name.value;

		dialog.close({
			name,
			basePath
		});
	}, [ dialog, fieldState, basePath ]);

	useEnhancedEffect(() => {
		if (inputRef.current && document.activeElement === inputRef.current) {
			inputRef.current.setSelectionRange(0, inputRef.current.value?.length);
		}
	}, []);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={handleSubmit} noValidate>
				<DialogHeader title={translations.title} icon="mdi/pencil" onClose={handleClose} />
				<DialogContent>
					<label className={baseClass.element("field-row").toClassName()}>
						<Typography className={baseClass.element("field-label").toClassName()}>
							{translations.controls.name}
						</Typography>
						<FieldText
							className={baseClass.element("field-input").toClassName()}
							value={fieldState.name.value}
							validationErrors={fieldState.name.validationErrors}
							onChange={setFieldState.name}
							autoFocus
							grow
							inputRef={inputRef}
						/>
					</label>

					<label className={baseClass.element("field-row-resource-list").toClassName()}>
						<ResourceList
							onSelect={selectResource}
							labelSearch={translations.controls.directory}
							allowedResourceTypes={allowedResourceTypes}
							selectedId={selectedParentNode?.id ?? "__root__"}
						/>
					</label>
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label={translations.actions.cancel}
						icon="mdi/close"
						type="button"
						style={ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
					/>
					<ActionButton
						label={translations.actions.confirm}
						icon="mdi/check"
						type="submit"
						style={ACTION_BUTTON_STYLE.PRIMARY}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleSubmit}
						disabled={
							fieldState.name.validationErrors.length > 0 ||
							fieldState.name.value === "" ||
							fieldState.name.pristine
						}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
